// pages/TransactionDetail.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import BannerCarousel from "../components/banner-carousel";
import SidebarHeader from "../components/SidebarHeader";
import BottomNav from "../components/bottom-menu";
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from "../utils/api";
import Bmnemonic_PNG from "../assets/image/Bmnemonic_PNG.png"
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

function TransactionDetail() {
    const location = useLocation();
    const navigate = useNavigate();
    const txn = location.state?.txn;

    if (!txn) {
        navigate(-1);
        return null;
    }

    // Map API response fields to component fields
    const {
        id,
        order_id,
        amount,
        consumerName,
        order_type,
        time,
        date,
        cn,
        user_profit,
        txn: transactionRef,
        status,
        biller_name,
        logo
    } = txn;

    // Get transaction type display for UI
    const getTransactionDisplay = (type) => {
        switch (type) {
            case "1":
                return "Payment";
            case "2":
                return "Mobile Recharge";
            case "3":
                return "Bill Payment";
            case "4":
                return "Bonus";
            default:
                return "Transaction";
        }
    };

    // Get status display
    const getStatusDisplay = (statusCode) => {
        switch (statusCode) {
            case "1":
                return { status: "success", label: "Success", color: COLORS.primaryColor };
            case "0":
                return { status: "pending", label: "Pending", color: COLORS.primaryColor };
            case "3":
                return { status: "failed", label: "Failed", color: COLORS.errorColor };
            default:
                return { status: "pending", label: "Unknown", color: COLORS.primaryColor };
        }
    };

    const statusInfo = getStatusDisplay(status);
    const isSuccess = statusInfo.status === "success";
    const isFailed = statusInfo.status === "failed";
    const isPending = statusInfo.status === "pending";
    const formattedAmount = parseFloat(amount).toFixed(2);

    const getStatusTitle = () => {
        if (isFailed) return `Transaction Failed`;
        if (isPending) return `Transaction Pending`;
        if (isSuccess) return `Transaction Successful`;
        return "Transaction Status";
    };

    const getIcon = () => {
        if (isSuccess) {
            return (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={COLORS.primaryColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            );
        } else if (isFailed) {
            return (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={COLORS.errorColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
            );
        } else {
            return (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={COLORS.primaryColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                </svg>
            );
        }
    };

    const getBgColor = () => {
        if (isSuccess) return `${COLORS.primaryColor}15`;
        if (isFailed) return `${COLORS.errorColor}15`;
        return `${COLORS.primaryColor}15`;
    };

    const getAmountColor = () => {
        if (isSuccess) return COLORS.primaryColor;
        if (isFailed) return COLORS.errorColor;
        return COLORS.primaryColor;
    };

    const formatDateTime = (dateStr, timeStr) => {
        if (dateStr && timeStr) {
            const dateTimeStr = `${dateStr} ${timeStr}`;
            const d = new Date(dateTimeStr);
            if (!isNaN(d.getTime())) {
                return d.toLocaleString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                });
            }
        }
        const d = new Date(dateStr);
        return d.toLocaleString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    };

    const formatTime = (timeStr) => {
        const d = new Date(`2000-01-01 ${timeStr}`);
        return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    };

    return (
        <SidebarHeader>
            <div className="flex items-center gap-3 pb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                >
                    <FiArrowLeft className="text-xl" />
                </button>
                <h1 className="text-lg font-semibold text-gray-800">
                    {getStatusTitle()}
                </h1>
            </div>
            <div className="max-w-7xl mx-auto mb-16 px-4">
                {/* Status Icon */}
                <div
                    className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: getBgColor() }}
                >
                    {getIcon()}
                </div>

                {/* Status Title */}
                <h2
                    className="text-lg font-bold mb-1 text-center"
                    style={{ color: COLORS.textPrimary }}
                >
                    {getStatusTitle()}
                </h2>

                {/* Amount */}
                <div
                    className="text-2xl font-extrabold mb-1 text-center"
                    style={{
                        color: getAmountColor(),
                    }}
                >
                    ₹{formattedAmount}
                </div>

                {/* Date */}
                <div
                    className="text-xs mb-6 text-center"
                    style={{ color: COLORS.textSecondary }}
                >
                    {formatDateTime(date, time)}
                </div>

                {/* Transaction Details Card */}
                <div
                    className="rounded-2xl p-5 border text-left mb-6"
                    style={{
                        background: COLORS.surfaceColor,
                        borderRadius: COLORS.borderRadius,
                        borderColor: COLORS.borderColor,
                    }}
                >
                    {/* Order ID */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                            Order ID
                        </div>
                        <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                            {order_id || id || "N/A"}
                        </div>
                    </div>

                    {/* Transaction Reference */}
                    {transactionRef && (
                        <div className="mb-3">
                            <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                Operator TXN/RRN
                            </div>
                            <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                                {transactionRef}
                            </div>
                        </div>
                    )}

                    {/* Transaction Type */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                            Service Type
                        </div>
                        <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                            {getTransactionDisplay(order_type)}
                        </div>
                    </div>

                    {/* Operator / Biller */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                            {order_type === "3" ? "Biller" : "Operator"}
                        </div>
                        <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                            {biller_name || "N/A"}
                        </div>
                    </div>

                    {/* Mobile Number / Consumer Number - Updated based on transaction type */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                            {order_type === "2" ? "Mobile Number" :
                                order_type === "3" ? "Consumer Number" :
                                    order_type === "1" ? "Payment ID" : "Reference ID"}
                        </div>
                        <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                            {cn || "N/A"}
                        </div>
                    </div>

                    {/* Consumer Name - Show for bill payments and recharges */}
                    {(consumerName && (order_type === "2" || order_type === "3")) && (
                        <div className="mb-3">
                            <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                Consumer Name
                            </div>
                            <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                                {consumerName}
                            </div>
                        </div>
                    )}

                    {/* Amount */}
                    <div className="mb-3">
                        <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                            Amount
                        </div>
                        <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                            ₹{formattedAmount}
                        </div>
                    </div>

                    {/* Cashback/Profit - Only show for non-bonus transactions */}
                    {(order_type !== "4" && user_profit && parseFloat(user_profit) > 0) && (
                        <div className="mb-3">
                            <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                                Cashback Earned
                            </div>
                            <div className="text-sm font-semibold" style={{ color: COLORS.primaryColor }}>
                                ₹{parseFloat(user_profit).toFixed(2)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons - Hide for bonus transactions */}
                {order_type !== "4" && (
                    <div className="flex gap-3">
                        <button
                            onClick={() => window.print()}
                            className="flex-1 h-12 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 active:scale-98"
                            style={{
                                background: COLORS.surfaceColor,
                                color: COLORS.textPrimary,
                                border: `1px solid ${COLORS.borderColor}`,
                                borderRadius: COLORS.borderRadius,
                            }}
                        >
                            Print Receipt
                        </button>
                        <button
                            onClick={() => navigate("/complaint", { state: { txnId: order_id || id } })}
                            className="flex-1 h-12 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 active:scale-98"
                            style={{
                                background: COLORS.primaryColor,
                                color: COLORS.textOnPrimary,
                                borderRadius: COLORS.borderRadius,
                            }}
                        >
                            Raise Complaint
                        </button>
                    </div>
                )}

                {/* Bonus specific message */}
                {order_type === "4" && (
                    <div className="mt-4 p-4 rounded-lg" style={{
                        background: `${COLORS.primaryColor}10`,
                        border: `1px solid ${COLORS.primaryColor}30`
                    }}>
                        <p className="text-sm" style={{ color: COLORS.textPrimary }}>
                            🎉 This is a bonus transaction. No action required.
                        </p>
                    </div>
                )}

                {/* Bottom Navigation - Hidden on desktop (lg and above) */}
                <div className="lg:hidden mt-4">
                    <BottomNav />
                </div>
            </div>

            <Toaster />

            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                `}
            </style>
        </SidebarHeader>
    );
}

export default TransactionDetail;